import * as moment from 'moment'
import { DateTime } from '../../../domain/DateTime'
import { Operation } from '../../../domain/Operation'
import { isTracepointActive, TracepointRule } from '../../../domain/TracepointRule'
import { parseTransactionName } from '../../../ui/transactionName'
import { ApplicationState } from '../../ApplicationState'
import { getTracepointRuleState } from '../V2State'

export interface TracepointRuleState {
    byId: { [key: number]: TracepointRule<DateTime> }
    byTransactionId: { [key: number]: number[] }
    loaded: boolean
}

export const getTracepointRules = (state: ApplicationState): Array<TracepointRule<moment.Moment>> =>
    getTracepointRulesByIds(
        state,
        Object.keys(getTracepointRuleState(state).byId).map((id: string) => parseInt(id, 10)),
    )

export const getActiveTracepointRuleForTransaction = (
    state: ApplicationState,
    transaction: Operation,
): Array<TracepointRule<moment.Moment>> => {
    const taggedTransaction = transaction.parentId !== null
    const id = transaction.parentId ? transaction.parentId : transaction.id
    const tracepointRuleIds = getTracepointRuleState(state).byTransactionId[id] || []

    const now = moment.utc()
    const rules = getTracepointRulesByIds(state, tracepointRuleIds).filter(rule => isTracepointActive(rule, now))

    if (taggedTransaction) {
        const tag = parseTransactionName(transaction.identifier).tag || 'invalid-tag'

        return rules.filter(rule => rule.transactionTags.includes(tag))
    }

    return rules.filter(rule => rule.transactionTags.length === 0)
}

export const getTracepointRulesByIds = (state: ApplicationState, ids: number[]): Array<TracepointRule<moment.Moment>> => {
    return ids
        .map(id => getTracepointRuleState(state).byId[id])
        .filter(rule => Boolean(rule))
        .map(rule => ({
            ...rule,
            createdAt: moment.utc(rule.createdAt.date),
            startAt: rule.startAt ? moment.utc(rule.startAt.date) : null,
            activeUntil: moment.utc(rule.activeUntil.date),
        }))
}

export const getTracepointById = (state: ApplicationState, id: number): TracepointRule<moment.Moment> | null => {
    const rule = getTracepointRuleState(state).byId[id]

    if (!rule) {
        return null
    }

    return {
        ...rule,
        createdAt: moment.utc(rule.createdAt.date),
        startAt: rule.startAt ? moment.utc(rule.startAt.date) : null,
        activeUntil: moment.utc(rule.activeUntil.date),
    }
}

export const areTracepointRulesLoaded = (state: ApplicationState): boolean => getTracepointRuleState(state).loaded

export const getNumberOfActiveTracepoints = (state: ApplicationState): number => {
    const ids = Object.keys(getTracepointRuleState(state).byId).map(id => parseInt(id, 10))
    const now = moment.utc()

    return getTracepointRulesByIds(state, ids).filter(rule => isTracepointActive(rule, now)).length
}
